import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useActiveModuleColor } from "@/contexts/ActiveModuleColorContext";
import { useKbAgentChat, type KbActionLog } from "@/hooks/useKbAgentChat";
import { X, Send, Loader2, RotateCcw, Eye } from "lucide-react";
import { useLocation, useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";

const ORBI_IMAGE = "/Orbi mascote/Orbi oficial 1.png";
const ORBI_TYPING_VIDEO = "/Orbi mascote/Orbi Digitando.mp4";
const CONFIRM_TAG = "[CONFIRMAR]";

function OrbiAvatar({
  size = "md",
  animation = "idle",
  className,
}: {
  size?: "sm" | "md" | "lg";
  animation?: "idle" | "thinking" | "pop" | "generating";
  className?: string;
}) {
  const sizeClass = size === "sm" ? "h-7 w-7" : size === "md" ? "h-8 w-8" : "h-10 w-10";
  const animationClass =
    animation === "idle"
      ? "animate-orbi-breath"
      : animation === "thinking"
        ? "animate-orbi-thinking"
        : animation === "generating"
          ? "animate-orbi-generating"
          : "animate-orbi-pop";

  return (
    <div
      className={cn(
        "rounded-full overflow-hidden flex-shrink-0 bg-primary/10 flex items-center justify-center",
        sizeClass,
        animationClass,
        className
      )}
    >
      <img src={ORBI_IMAGE} alt="Orbi" className="h-full w-full object-cover" />
    </div>
  );
}

function extractConfirmTag(content: string): { text: string; needsConfirm: boolean } {
  const idx = content.indexOf(CONFIRM_TAG);
  if (idx === -1) return { text: content, needsConfirm: false };
  return { text: content.slice(0, idx).trimEnd(), needsConfirm: true };
}

function parseInline(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**"))
      return <strong key={i} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
    if ((part.startsWith("*") && part.endsWith("*")) || (part.startsWith("_") && part.endsWith("_")))
      return <em key={i} className="italic">{part.slice(1, -1)}</em>;
    return part;
  });
}

function renderContent(content: string) {
  const lines = content.split("\n");
  const nodes: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const raw = lines[i];
    const trimmed = raw.trim();

    const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const cls =
        level === 1
          ? "text-sm font-bold text-foreground mt-2 mb-1"
          : level === 2
            ? "text-xs font-bold text-foreground mt-2 mb-1"
            : "text-xs font-semibold text-foreground/80 mt-1.5 mb-0.5";
      nodes.push(<div key={i} className={cls}>{parseInline(headingMatch[2])}</div>);
      i++; continue;
    }

    if (/^-{3,}$/.test(trimmed)) {
      nodes.push(<hr key={i} className="my-2 border-border/50" />);
      i++; continue;
    }

    if (trimmed.startsWith("- ") || trimmed.startsWith("• ")) {
      const items: React.ReactNode[] = [];
      while (i < lines.length && (lines[i].trim().startsWith("- ") || lines[i].trim().startsWith("• "))) {
        const text = lines[i].trim().substring(2);
        items.push(
          <li key={i} className="flex items-start gap-1.5">
            <span className="mt-[5px] h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
            <span>{parseInline(text)}</span>
          </li>
        );
        i++;
      }
      nodes.push(<ul key={`ul-${i}`} className="space-y-0.5 my-1">{items}</ul>);
      continue;
    }

    if (/^\d+\.\s/.test(trimmed)) {
      const items: React.ReactNode[] = [];
      let counter = 1;
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
        const match = lines[i].trim().match(/^\d+\.\s(.*)/);
        if (match) {
          items.push(
            <li key={i} className="flex items-start gap-1.5">
              <span className="font-bold text-primary min-w-[1.1rem] text-xs">{counter}.</span>
              <span>{parseInline(match[1])}</span>
            </li>
          );
          counter++;
        }
        i++;
      }
      nodes.push(<ol key={`ol-${i}`} className="space-y-0.5 my-1">{items}</ol>);
      continue;
    }

    if (!trimmed) {
      nodes.push(<div key={i} className="h-1.5" />);
      i++; continue;
    }

    nodes.push(<div key={i} className="leading-relaxed">{parseInline(trimmed)}</div>);
    i++;
  }

  return <div className="space-y-0.5 text-xs">{nodes}</div>;
}

const WRITE_TYPES = new Set([
  "patch_briefing_field", "patch_prompt_field", "update_flow_prompt",
  "add_prompt_rule", "patch_document_chunks", "manage_knowledge_url",
  "generate_agent", "create_knowledge_document", "manage_api", "rate_conversation",
]);

function ActionLogEntry({ action }: { action: KbActionLog }) {
  const d = action.detail as Record<string, unknown>;
  const isWrite = WRITE_TYPES.has(action.type);
  const label = (() => {
    switch (action.type) {
      case "list_documents": return `Consultou docs (${d?.total ?? 0} indexados)`;
      case "get_briefing": return `Consultou briefing`;
      case "get_flow_prompt": return `Consultou prompt`;
      case "search_knowledge": return `Buscou na base`;
      case "get_gallery_items": return `Consultou galeria`;
      case "list_apis": return `Listou APIs (${d?.total ?? 0})`;
      case "list_conversations": return `Listou conversas (${d?.total ?? 0})`;
      case "get_conversation_messages": return `Leu conversa`;
      case "generate_agent": return `Gerou agente${(d?.agent_name) ? ` "${d.agent_name}"` : ""}${d?.success === false ? " ⚠️" : ""}`;
      case "patch_briefing_field": return `Briefing.${d?.briefing_field ?? d?.field} — ${d?.change_summary ?? ""}${d?.success === false ? " ⚠️" : ""}`;
      case "patch_prompt_field": return `Alterou "${d?.field}" — ${d?.change_summary ?? ""}${d?.success === false ? " ⚠️" : ""}`;
      case "add_prompt_rule": return `Regra adicionada — ${d?.change_summary ?? ""}${d?.success === false ? " ⚠️" : ""}`;
      case "manage_api": return `${d?.action === "add" ? "Adicionou" : "Removeu"} API${d?.success === false ? " ⚠️" : ""}`;
      case "rate_conversation": return `Avaliou conversa — ${d?.rating ?? ""}${d?.success === false ? " ⚠️" : ""}`;
      case "create_knowledge_document": return `Criou documento: ${d?.file_name ?? ""}${d?.success === false ? " ⚠️" : ""}`;
      default: return action.type;
    }
  })();

  return (
    <div className={cn("flex items-start gap-1 text-[10px] leading-tight", isWrite ? "text-amber-600" : "text-muted-foreground")}>
      <Eye className="h-2.5 w-2.5 shrink-0 mt-0.5" />
      <span>{label}</span>
    </div>
  );
}

export function DocAssistantWidget() {
  const { color: activeModuleColor } = useActiveModuleColor();
  const { messages, sending, sendMessage, clearSession } = useKbAgentChat();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    sendMessage(text);
  };

  const isChatsPage = location.pathname === "/chat";
  const hasOpenChat = searchParams.get("chatId");
  const shouldHide = isChatsPage && !!hasOpenChat;

  return (
    <>
      {/* Botão flutuante */}
      <Button
        type="button"
        size="icon"
        className={cn(
          "fixed z-[180] rounded-full h-16 w-16 p-0 shadow-[0_0_20px_rgba(0,0,0,0.2)] border-4 overflow-hidden",
          "bg-white hover:bg-white",
          "bottom-6 right-6 transition-all duration-300 hover:scale-110 hover:shadow-[0_0_30px_rgba(0,0,0,0.3)]",
          open && "rotate-90 scale-90 opacity-0 pointer-events-none",
          shouldHide && "opacity-0 scale-0 pointer-events-none translate-y-10"
        )}
        style={{ borderColor: activeModuleColor || "#ffffff" }}
        onClick={() => setOpen(true)}
        aria-label="Orbi IA"
      >
        <img src={ORBI_IMAGE} alt="Orbi" className="h-full w-full object-cover scale-125" />
      </Button>

      {/* Painel do chat */}
      <div
        className={cn(
          "fixed z-[190] bottom-6 right-6 w-[min(calc(100vw-2rem),400px)]",
          "rounded-2xl border bg-background shadow-2xl flex flex-col overflow-hidden transition-all duration-300 origin-bottom-right",
          open ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-10 pointer-events-none",
          shouldHide && "opacity-0 scale-95 translate-y-10 pointer-events-none"
        )}
        style={{ maxHeight: "calc(100vh - 2rem)", height: "600px" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 bg-gradient-to-r from-primary/10 via-primary/5 to-background border-b shrink-0">
          <div className="flex items-center gap-3">
            <OrbiAvatar size="md" animation="idle" />
            <div>
              <h3 className="text-sm font-semibold">Orbi IA</h3>
              <p className="text-[10px] text-muted-foreground leading-none">Assistente do Prompt Builder</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 rounded-full hover:bg-muted/80"
                onClick={clearSession}
                title="Limpar conversa"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full hover:bg-muted/80"
              onClick={() => setOpen(false)}
              aria-label="Fechar"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4 bg-muted/5 scroll-smooth">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-6 space-y-3 opacity-70">
              <OrbiAvatar size="lg" animation="idle" className="h-14 w-14" />
              <div className="space-y-1">
                <p className="text-sm font-medium">Olá! Como posso ajudar?</p>
                <p className="text-xs text-muted-foreground">
                  Pergunte sobre seu agente, briefing, base de conhecimento ou use as ferramentas do Prompt Builder.
                </p>
              </div>
              <div className="flex flex-col gap-1.5 w-full mt-1">
                {[
                  "Qual o nome do meu agente?",
                  "Listar documentos da base",
                  "Mostrar briefing atual",
                  "Mostrar prompt atual",
                ].map((hint) => (
                  <button
                    key={hint}
                    type="button"
                    onClick={() => { setInput(hint); inputRef.current?.focus(); }}
                    className="text-xs px-3 py-1.5 rounded-lg border border-border hover:bg-muted/60 hover:border-primary/30 text-muted-foreground hover:text-foreground transition-colors text-left"
                  >
                    {hint}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg, msgIdx) => {
              const isLastAssistant = msg.role === "assistant" && msgIdx === messages.length - 1;
              const { text: cleanContent, needsConfirm } =
                msg.role === "assistant"
                  ? extractConfirmTag(msg.content)
                  : { text: msg.content, needsConfirm: false };

              return (
                <div key={msg.id} className="space-y-1">
                  <div className={cn("flex w-full gap-2", msg.role === "user" ? "justify-end" : "justify-start")}>
                    {msg.role === "assistant" && <OrbiAvatar size="sm" animation="pop" className="mt-0.5" />}
                    <div
                      className={cn(
                        "max-w-[85%] px-4 py-3 text-sm shadow-sm",
                        msg.role === "user"
                          ? "bg-primary text-primary-foreground rounded-2xl rounded-tr-sm"
                          : "bg-card border text-card-foreground rounded-2xl rounded-tl-sm"
                      )}
                    >
                      {msg.role === "assistant"
                        ? renderContent(cleanContent)
                        : <div className="whitespace-pre-wrap">{msg.content}</div>}
                    </div>
                  </div>

                  {/* Botões de confirmação */}
                  {needsConfirm && isLastAssistant && !sending && (
                    <div className="pl-10 flex gap-2 pt-1">
                      <Button
                        size="sm"
                        className="h-7 rounded-full px-3 text-xs"
                        onClick={() => sendMessage("Sim, pode fazer")}
                      >
                        Confirmar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 rounded-full px-3 text-xs"
                        onClick={() => sendMessage("Cancelar")}
                      >
                        Cancelar
                      </Button>
                    </div>
                  )}

                  {/* Action logs */}
                  {msg.role === "assistant" && msg.actions && msg.actions.length > 0 && (
                    <div className="pl-10 space-y-0.5">
                      {msg.actions.map((action, i) => (
                        <ActionLogEntry key={i} action={action} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}

          {/* Typing indicator */}
          {sending && (
            <div className="flex w-full justify-start gap-2">
              <div className="h-7 w-7 flex-shrink-0 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center mt-0.5">
                <video
                  src={ORBI_TYPING_VIDEO}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="h-full w-full object-cover"
                  aria-hidden
                />
              </div>
              <div className="bg-card border px-4 py-3 rounded-2xl rounded-tl-sm shadow-sm flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce [animation-delay:-0.3s]" />
                <span className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce [animation-delay:-0.15s]" />
                <span className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce" />
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Input */}
        <div className="p-4 bg-background border-t shrink-0">
          <form
            className="relative flex items-center"
            onSubmit={(e) => { e.preventDefault(); handleSend(); }}
          >
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSend(); } }}
              placeholder="Pergunte sobre seu agente..."
              className="w-full h-11 pl-4 pr-12 rounded-full border bg-muted/30 focus:bg-background outline-none focus:ring-2 focus:ring-primary/20 transition-all text-sm"
              disabled={sending}
            />
            <Button
              type="submit"
              size="icon"
              className={cn(
                "absolute right-1.5 h-8 w-8 rounded-full transition-all",
                input.trim() ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted"
              )}
              disabled={sending || !input.trim()}
            >
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5 ml-0.5" />}
            </Button>
          </form>
        </div>
      </div>
    </>
  );
}
